<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { watch, computed, ref, onBeforeUnmount } from "vue"
import { useRoute } from "vue-router"
import { ConfigGraphCard, ConditionBlock } from "@types"
import {
    DDA_STATUS,
    SOURCES_TYPES,
    SOURCE_WITHOUT_RAW_FREQ,
    SOURCE_WITHOUT_HOURLY_FREQ,
    TASK_REBUILD_SOURCE_DATA,
} from "@core/constants"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useSourceStore } from "@core/stores/source.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useTaskStore } from "@core/stores/task.store"
import { SourceService } from "@core/services"
import { Freq } from "@types"
import {
    useFetchWrapper,
    useKPI,
    useWorkflowEnedisMessage,
} from "@core/composables"
import { formatIdSource, formatDate } from "@core/helpers"
import {
    handleSourceCondition,
    handleUserCondition,
    sertLaCauseCompteurInexistant,
} from "@modules/data/helpers"
import { useI18n } from "vue-i18n"
import { useEventBus } from "@vueuse/core"
import type { InformationPDL, NotificationEvent } from "@types"
import {
    evaluateVisibleConditionsBlocks,
    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
} from "@modules/data/utils/evaluateVisibleConditionsBlocks"
import SourceDataEnedisView from "@modules/data/components/data/enedis/SourceDataEnedisView.vue"
import type { SegmentEnedis } from "@modules/data/utils/ongletsEnedis"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"
import { useInterrupteursStore } from "@core/stores/interrupteurs.store"
import {
    contexteDeLaPageCompteur,
    laPageCompteurV2EstServie,
    JOURS_DU_CONTEXTE,
} from "@modules/data/utils/pageCompteurV2"
import {
    lireInformationsPdl,
    segmentDuPdl,
    directionsDuPdl,
    variablesServiesDuPdl,
} from "@modules/data/utils/informationsPdl"
import SourceDataGrdfView, {
    type DataSourceView,
} from "@modules/data/components/data/grdf/SourceDataGrdfView.vue"
import {
    completudeDeLaPeriode,
    plagesDuVerdict,
    unFaitDeTrouEstServi,
} from "@modules/data/utils/completude"
import { fetchSourceDataGrdf } from "@modules/data/services/sourceDataGrdf.service"
import {
    choisirFrequence,
    pasDetenusDeLaSource,
    type GraphiquePourFrequence,
} from "@modules/data/utils/frequenceParDefaut"
import {
    categorieGraphesUBordeaux,
    fluideUBordeaux,
} from "@core/components/source/meter/display/card/meter-ubordeaux.helper"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: ({ named }: { named: (key: string) => string }) => {
                const typeMapping = {
                    pdl: "du compteur",
                    pce: "du compteur",
                    idopenmeteo: "de la station météo",
                    idmeteofrance: "de la station météo",
                    cpcu: "du compteur",
                    dalkia: "du compteur",
                    sigdu: "du compteur",
                } as Record<string, string>

                return `Données ${typeMapping[named("idtype")] ?? "du capteur"}`
            },
            export: "Exporter les données",
            reload: "Recharger les données",
            empty: "Veuillez sélectionner un graphique pour afficher les données.",
            all: "Ajouter tous les graphiques",
            pce_freq_missing:
                'Les données du compteur ne sont pas encore disponibles. Cliquer sur le bouton "Recharger les données". pour obtenir les dernières données.',
            not_exist: `Enedis nous a remonté que le compteur électrique <strong>{idsource}</strong> n'existe pas sur le réseau. Nous vous invitons à contacter Enedis au <a href="tel:09 70 83 19 70"><strong class="strong--small">09 70 83 19 70</strong></a> et dire "Je souhaite parler à un conseiller." pour comprendre la situation.`,
            consent_request:
                "Une demande de consentement est en attente pour ce compteur.",
            grdf_error:
                "Impossible de charger les données du compteur sur cette période.",
            reload_success_summary: "Données mises à jour",
            reload_success_detail:
                "La récupération est terminée : les données affichées viennent d'être actualisées.",
            reload_error_summary: "Récupération impossible",
            reload_error_detail:
                "La récupération n'a pas pu aboutir. Merci de réessayer dans quelques minutes ; si le problème persiste, contactez le support.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const graphStore = useGraphStore()
const sourceStore = useSourceStore()
const taskStore = useTaskStore()
const authStore = useAuthStore()
const sourceService = new SourceService()
const route = useRoute()
/**
 * Bus de notification applicatif : `App.vue` le mappe sur le toast
 * `group: "notifications"`. C'est le SEUL chemin visible — les conteneurs
 * `<BaseToast>` montés par l'app sont tous groupés, un `toast.add()` sans
 * `group` n'atterrirait dans aucun conteneur.
 */
const notificationBus = useEventBus<NotificationEvent>("notification")

/**
 * Bannière d'état de récupération Enedis (DEV-1225/1489/1502, avancement
 * DEV-1499). Elle n'existait QUE sur la page bâtiment : la page du COMPTEUR
 * — celle qu'ouvre le client qui cherche pourquoi SES courbes s'arrêtent —
 * n'affichait rien. Un compteur résilié, sans consentement ou en retard
 * confirmé y était muet, alors que le composable supporte le mode `source`
 * depuis l'origine (il n'avait simplement aucun appelant).
 */
const {
    getWorkflowEnedisMessageList,
    fetch: fetchWorkflowEnedis,
    workflowEnedisList,
} = useWorkflowEnedisMessage("source")

/**
 * Vrai dès que la lecture du verdict est TERMINÉE, qu'elle ait rendu un verdict
 * ou non (DEV-1508 lot 3c).
 *
 * `verdictDeLaSource === null` ne le dit pas : c'est aussi ce que vaut un
 * verdict pas encore lu. La bande de complétude a besoin de la différence, sans
 * quoi elle peint « Manquant » sur des jours dont le verdict dira, une seconde
 * plus tard, qu'ils sont en attente de collecte. L'adresse ombre tient la même
 * distinction, de la même façon.
 */
const verdictRecu = ref(false)

const { fetch: fetchInitialData, isFetching: isFetchingInitialData } =
    useFetchWrapper(async (id: string) => {
        graphStore.setCurrentSourceId(id)
        // ⚠️ APRÈS `setCurrentSourceId` : le composable lit le PRM courant dans
        // le store graph, un appel plus tôt partirait sur `null`.
        verdictRecu.value = false
        // La lecture ne lève pas : elle se termine même en erreur, et c'est bien
        // « terminée » qu'on annonce, pas « réussie ».
        void Promise.resolve(fetchWorkflowEnedis()).finally(() => {
            verdictRecu.value = true
        })
        if (!graphStore.isGraphListLoaded) {
            await graphStore.fetchGraphList()
        }
    })

// ================================
// COMPUTED
// ================================
/** Id de la source depuis la route (string ou premier élément si tableau). */
const sourceIdParam = computed(() => {
    const p = route.params._id_source
    return Array.isArray(p) ? p[0] : p
})

/** Source courante chargée depuis le store (réactive après fetchSourceById). */
const currentSource = computed(() => {
    const id = sourceIdParam.value
    return id ? sourceStore.getSourceById(id) : undefined
})

// ================================
// GRDF gaz (PCE) — page de données brutes dédiée (DEV-595)
// ================================
/** Source = compteur gaz GRDF (PCE) → vue brute dédiée (Facturation/Suivi).
 *  On teste `idtype` ET `type` : dans le store la source est fusionnée
 *  `{...sourceDapi, ...sourceLegacy}` et le `type` legacy (souvent null) écrase
 *  le `type` calculé par dapi → `idtype` ("pce") est le champ fiable. */
const isGrdfGas = computed(() => {
    const s = currentSource.value
    return s?.idtype === SOURCES_TYPES.PCE || s?.type === SOURCES_TYPES.PCE
})

// ================================
// Enedis (PDL) — page « Données du compteur » v2 (DEV-1508 lot 2)
// ================================
/** Source = compteur Enedis (PDL) ET interrupteur armé → nouvelle vue.
 *
 *  Engagement E11 : l'interrupteur désarmé laisse la page STRICTEMENT celle
 *  d'avant. C'est pourquoi le flag est le PREMIER terme du test : désarmé, la
 *  branche n'est jamais prise, aucun composant v2 n'est monté, aucun appel de
 *  plus ne part. Depuis le lot 3 il se lit au runtime, par société : le dyno dit
 *  la population armée, la page demande seulement « suis-je dedans ? ». Tant que
 *  la réponse n'est pas là, ou si elle n'arrive jamais, c'est non.
 *
 *  Le test d'identité porte sur `idtype`, comme pour le gaz : dans le store la
 *  source est fusionnée `{...sourceDapi, ...sourceLegacy}` et le `type` legacy
 *  (souvent null) écrase celui calculé par dapi. Il ne porte QUE sur `pdl` :
 *  d'autres types (ELECSAVOIE, CPCU…) partagent des chemins de graphes avec le
 *  PDL, ils ne partagent pas cette page. */
const interrupteurs = useInterrupteursStore()

const isEnedisPdlV2 = computed(() =>
    laPageCompteurV2EstServie({
        arme: interrupteurs.enedisPageDonneesV2Arme,
        source: currentSource.value ?? null,
    })
)

/** Informations PDL servies pour ce compteur (en-tête de la vue v2).
 *
 *  Lues À LEUR PLACE, `informations.pdl`, et NON par le getter générique
 *  `getInformationByType()` : celui-ci indexe `informations` par le `type`
 *  fusionné, qui est justement le champ écrasé à null par le legacy. Sur un
 *  compteur reconnu par son `idtype`, il rendrait null, le segment retomberait
 *  sur « INDET » et les sens sur « soutirage » seul, effaçant l'injection d'un
 *  point qui injecte. Le piège et son remède sont tenus par
 *  `utils/informationsPdl.spec.ts`. */
const informationsPdl = computed<InformationPDL | null>(() => {
    if (!isEnedisPdlV2.value) return null
    return lireInformationsPdl(currentSource.value)
})

/** Segment du point. Absent se lit « je ne sais pas », jamais un segment par défaut. */
const segmentPdl = computed<SegmentEnedis>(() =>
    segmentDuPdl(informationsPdl.value)
)

/** Les sens que ce compteur porte RÉELLEMENT (E5) : voir `informationsPdl.ts`. */
const directionsPdl = computed<DirectionEnedis[]>(() =>
    directionsDuPdl(informationsPdl.value)
)

/**
 * Les variables réellement servies pour ce compteur, par sens : ce sont elles
 * qui décident des onglets et des séries (E1), jamais le segment seul.
 */
const variablesServiesPdl = computed(() =>
    variablesServiesDuPdl(informationsPdl.value)
)

/** Date → YYYY-MM-DD (local, sans décalage TZ). */
function toYmd(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    return `${d.getFullYear()}-${m}-${day}`
}
/**
 * Le CONTEXTE de la page « Données du compteur » : le MÊME que celui de
 * l'adresse ombre, par la même fonction (DEV-1508 lot 3c).
 *
 * Il lisait jusqu'ici `graphStore.period`, c'est-à-dire la période du sélecteur
 * de la page de données standard — trois mois, que la v2 n'affiche pas et que
 * personne n'avait choisis. La page armée ouvrait donc sur « du 11/06/2026 au
 * 11/09/2026 » quand l'ombre ouvrait sur un an (staging, 11/09). Deux entrées,
 * deux fenêtres, la même page : c'est un des deux écrans qui ment.
 */
/**
 * LA LARGEUR DU CONTEXTE, en jours : ce que le raccourci pressé demande.
 *
 * Elle vit ICI, avec le calcul du contexte, et pas dans la vue : c'est la même
 * fonction qui sait où le dû s'arrête pour les deux entrées (lot 5c, écart B3).
 */
const joursDuContextePdl = ref(JOURS_DU_CONTEXTE)

const contextePdl = computed(() =>
    // Les informations descendent AUSSI : la borne `data.<sens>.date_from` du
    // sens entre dans le calcul de la première donnée détenue (lot 5a, D4).
    contexteDeLaPageCompteur(
        variablesServiesPdl.value,
        undefined,
        informationsPdl.value,
        joursDuContextePdl.value
    )
)

const grdfDateFrom = computed(() => toYmd(graphStore.period[0]))
const grdfDateTo = computed(() => toYmd(graphStore.period[1]))

const grdfSources = ref<DataSourceView[]>([])
const grdfLoading = ref(false)
const grdfError = ref(false)

/** Charge les 2 vues GRDF (Facturation / Suivi) via la route dapi `/datav2/series`. */
async function loadGrdf(): Promise<void> {
    const idSource = currentSource.value?._id_source
    if (!isGrdfGas.value || !idSource) return
    grdfLoading.value = true
    grdfError.value = false
    try {
        grdfSources.value = await fetchSourceDataGrdf(
            idSource,
            grdfDateFrom.value,
            grdfDateTo.value
        )
    } catch {
        // On NE vide PAS `grdfSources` : on garde les données précédentes
        // affichées (le composant reste monté) en cas d'échec d'un refetch.
        grdfError.value = true
    } finally {
        grdfLoading.value = false
    }
}

/** Sélection de période libre (calendrier range) → recadre la fenêtre → recharge. */
function onGrdfPeriod(period: [Date, Date]): void {
    graphStore.period = period
}

// (re)charge dès que la source GRDF ou la fenêtre change
watch(
    [isGrdfGas, () => currentSource.value?._id_source, () => graphStore.period],
    () => {
        if (isGrdfGas.value) void loadGrdf()
    },
    { immediate: true }
)

/**
 * Récupère la liste des graphiques de configuration en fonction du type de source.
 */
const getGraphConfigList = computed<Record<string, ConfigGraphCard> | null>(
    () => {
        if (!currentSource.value?.type) return null
        switch (currentSource.value?.type) {
            case SOURCES_TYPES.PDL:
                return graphStore.getGraphConfigByCategory("GRAPH_PDL")

            case SOURCES_TYPES.ELECSAVOIE:
                return graphStore.getGraphConfigByCategory("GRAPH_ELECSAVOIE")
            case SOURCES_TYPES.ELECSTRASBOURG:
                return graphStore.getGraphConfigByCategory(
                    "GRAPH_ELECSTRASBOURG"
                )
            case SOURCES_TYPES.PCE:
                return graphStore.getGraphConfigByCategory("GRAPH_PCE")

            case SOURCES_TYPES.REGAZ:
                return graphStore.getGraphConfigByCategory("GRAPH_REGAZ")

            case SOURCES_TYPES.CPCU:
            case SOURCES_TYPES.DALKIA:
                return graphStore.getGraphConfigByCategory("GRAPH_RCU")
            case SOURCES_TYPES.SIGDU:
                return graphStore.getGraphConfigByCategory("GRAPH_EAU")
            case SOURCES_TYPES.UBORDEAUX: {
                // Selon le fluide servi (DEV-2100) ; fluide null : aucun graphique.
                const categorie = categorieGraphesUBordeaux(
                    fluideUBordeaux(
                        currentSource.value?.informations?.ubordeaux
                    )
                )
                return categorie
                    ? graphStore.getGraphConfigByCategory(categorie)
                    : null
            }
            case SOURCES_TYPES.METEOFRANCE:
            case SOURCES_TYPES.OPENMETEO:
            case SOURCES_TYPES.METEO:
                return graphStore.getGraphConfigByCategory(
                    "GRAPH_WEATHER_STATION"
                )

            default:
                return graphStore.getGraphConfigByCategory("GRAPH_IOT")
        }
    }
)

/**
 * Liste des cartes de graphiques disponibles en appliquant les conditions de visibilité.
 */
const getGraphCardListAvailable = computed(() => {
    return (
        Object.values(getGraphConfigList.value ?? [])
            // Filtre les cartes de graphiques en fonction des conditions de visibilité
            .filter((graphCard) => {
                // si un paramétre graph à été passé en query on affiche ceux passé en paramétres
                if (graphStore?.getGraphQuery?.length > 0) {
                    return graphStore.getGraphQuery.includes(graphCard.id)
                }

                if (graphCard.visible_conditions?.length === 0) return true
                return evaluateVisibleConditionsBlocks(
                    graphCard.visible_conditions,
                    graphCard.visible_conditions_combine,
                    VISIBLE_CONDITIONS_COMBINE_CARD_DEFAULT,
                    (condition: ConditionBlock) => {
                        if (condition.origin === "user") {
                            return handleUserCondition(condition)
                        }
                        if (!currentSource.value) return false
                        return handleSourceCondition(
                            [currentSource.value],
                            condition
                        )
                    }
                )
            })
            // Trie les graphiques dont au moins 1 des variables des datasets est présente dans le champ _variable de la source
            .filter((graphCard) => {
                // Récupère les variables dans le champ dataset
                const variables = graphCard.graph_list
                    .flatMap((dataset) => {
                        return dataset.datasets.map((dataset) => {
                            return dataset.type?.split(".")[0] ?? ""
                        })
                    })
                    .filter((variable) => variable !== "") as string[]

                // Vérifie si au moins une des variables est présente dans le champ _variable de la source
                const sourceVariables = sourceStore.getVariablesBySourceId(
                    currentSource.value?._id_source ?? ""
                )
                return (
                    sourceVariables.filter((type: string) =>
                        variables.includes(type)
                    ).length > 0
                )
            })
            // Map pour ajouter la liste des graphiques à cacher
            .map((graphCard) => {
                return {
                    ...graphCard,
                    // Liste des graphiques à cacher car pas de variables disponibles dans le champ _variable de la source
                    graphHidden: graphCard.graph_list
                        .filter((graphItem) => {
                            // Récupère les variables dans le champ dataset
                            const variables = graphItem.datasets
                                .map((dataset) => {
                                    return dataset.type?.split(".")[0] ?? ""
                                })
                                .filter(
                                    (variable) => variable !== ""
                                ) as string[]
                            const sourceVariables =
                                sourceStore.getVariablesBySourceId(
                                    currentSource.value?._id_source ?? ""
                                )
                            // Vérifie si au moins une des variables est présente dans le champ _variable de la source
                            return (
                                sourceVariables.filter((type: string) =>
                                    variables.includes(type)
                                ).length === 0 &&
                                !graphItem.id?.includes("cout")
                            )
                        })
                        .map((graphItem) => {
                            return graphItem.id
                        }),
                }
            })
            .map((graphCard) => {
                return {
                    graphId: graphCard.id,
                    graphHidden: graphCard.graphHidden,
                }
            })
    )
})

/**
 * Liste des graphiques sélectionnés dans le sélecteur multiple.
 */
const getGraphSelected = computed(() => {
    return getGraphCardListAvailable.value.filter((graph) =>
        graphStore.graphs.includes(graph.graphId)
    )
})

/**
 * Ce que chaque carte sélectionnée affiche par défaut (son premier graphique
 * non masqué, cf. `DataChartListItem`) : ses fréquences et ses variables.
 * Sert à choisir une fréquence que les graphiques savent tracer (DEV-2071).
 */
const getGraphiquesPourFrequence = computed<GraphiquePourFrequence[]>(() => {
    const cartes = Object.values(getGraphConfigList.value ?? {})
    return getGraphSelected.value.flatMap(({ graphId, graphHidden }) => {
        const carte = cartes.find((c) => c.id === graphId)
        const affiche = carte?.graph_list.find(
            (g) => !graphHidden.includes(g.id)
        )
        if (!affiche) return []
        return [
            {
                freq: affiche.freq,
                variables: affiche.datasets
                    .map((d) => d.type?.split(".")[0] ?? "")
                    .filter((v) => v !== ""),
            },
        ]
    })
})

/**
 * Fréquence à appliquer pour les graphiques sélectionnés (DEV-2071) : la
 * courante si un graphique la propose, sinon la plus fine proposée pour
 * laquelle la source a des données.
 * @param courante - Fréquence choisie ou mémorisée (`null` = aucune)
 */
function frequencePourLesGraphiques(courante: Freq | null): Freq | null {
    return choisirFrequence({
        courante,
        frequencesPage: getAvailableFreqs.value,
        graphiques: getGraphiquesPourFrequence.value,
        pasDetenus: pasDetenusDeLaSource(
            currentSource.value?.informations as
                | Record<string, unknown>
                | undefined
        ),
    })
}

/**
 * Récupère la liste des options de carte de graphiques disponibles
 */
const getCardGraphOptionList = computed(() => {
    return getGraphCardListAvailable.value.map((graph) => graph.graphId)
})

/**
 * Récupère la liste des fréquences disponibles selon le type de source
 * Exclut "raw" pour les sources CPCU et REGAZ
 */
const getAvailableFreqs = computed(() => {
    let allFreqs = ["raw", "hourly", "daily", "weekly", "monthly"]

    // Si la source n'est pas encore chargée on affiche toutes les fréquences
    if (!currentSource.value?.type) {
        return allFreqs
    }

    // Cas particulier pour les sources qui n'ont pas de fréquence raw
    if (SOURCE_WITHOUT_RAW_FREQ.includes(currentSource.value.type)) {
        allFreqs = allFreqs.filter((freq) => freq !== "raw")
    }
    // Cas particulier pour les sources qui n'ont pas de fréquence horaire
    if (SOURCE_WITHOUT_HOURLY_FREQ.includes(currentSource.value.type)) {
        allFreqs = allFreqs.filter((freq) => freq !== "hourly")
    }
    return allFreqs
})

/**
 * Message global affiché en haut de la page.
 * - si le compteur n'existe pas
 */
const getGlobalMessage = computed<{
    message: string
    severity: string
    closable?: boolean
    action?: { fn: () => void; message: string; severity?: string }
    position: string
} | null>(() => {
    const source = currentSource.value
    if (
        [SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            source?.type as string
        ) &&
        source?.consentrequest
    ) {
        return {
            message: t("consent_request", {
                idsource: formatIdSource(
                    source?.idsource as string,
                    source?.type as string
                ),
            }),
            severity: "warn",
            position: "bottom",
        }
    } else if (
        source?.type === SOURCES_TYPES.PDL &&
        // DEV-1619 — UNE cause par compteur, la plus précise. La condition
        // servie jusqu'ici était `etat !== "en_service"`, qui range `resilie` et
        // `inexistant` dans le même sac : un compteur RÉSILIÉ lisait « n'existe
        // pas sur le réseau » avec un numéro de dépannage, sous la bannière de
        // workflow qui disait, elle, « résilié ou hors service ». Deux messages
        // contradictoires sur le même écran, dont le second faux d'après nos
        // propres faits (verdict `existe: true`). Le détail de la règle et les
        // faits datés sont dans le helper.
        sertLaCauseCompteurInexistant({
            etatPdl: source?.informations?.pdl?.etat,
            aUneDemandeDeConsentement: !!source?.consentrequest,
            uneCauseEstDejaNommee:
                getWorkflowEnedisMessageList.value.length > 0,
        })
    ) {
        return {
            message: t("not_exist", {
                idsource: formatIdSource(
                    source?.idsource as string,
                    source?.type as string
                ),
            }),
            severity: "error",
            position: "bottom",
        }
        // ⚠️ La branche « CDC en cours d'activation » vivait ici. Elle est
        // retirée parce qu'elle est INATTEIGNABLE, et NON parce qu'une autre
        // surface reprendrait le cas : elle testait
        // `data_retrieval.historical.state === "delayed"`, valeur qu'aucun
        // écrivain n'émet depuis DEV-1324 (dapi `_etat_historical()`, sha
        // 208975a3 lu le 31/08/2026, ne rend que
        // `noconsent | finished | crashed | waiting`, ou `null`). La bannière
        // Enedis ci-dessous ne le porte pas davantage : sa copie
        // `cdc_activating_delayed` est retirée par le même constat. Le cas
        // « CDC en cours d'activation » n'a aujourd'hui AUCUN producteur — le
        // rétablir suppose de rétablir d'abord son écrivain.
    } else if (getGraphCardListAvailable.value.length === 0) {
        return {
            message: t("empty"),
            severity: "info",
            action: {
                fn: onClickAddOptimalGraphs,
                message: t("all"),
            },
            position: "bottom",
        }
    }

    // Vérifie si la source est un compteur de gaz avec un état de droit d'accès Active mais sans fréquence dans les données contractuelles
    else if (
        source?.type === SOURCES_TYPES.PCE &&
        source?.informations?.pce?.droit_access?.etat === DDA_STATUS.ACTIVE &&
        !source?.informations?.pce?.compteur?.frequence
    ) {
        return {
            message: t("pce_freq_missing"),
            severity: "warn",
            closable: true,
            action: {
                fn: onClickReload,
                message: t("reload"),
                severity: "warn",
            },
            position: "top",
        }
    }

    return null
})

// ================================
// COMPOSABLES (KPI)
// ================================
const {
    indicators,
    onClickKPI,
    fetchKPIValue,
    loading: loadingKPI,
    error: errorKPI,
    rows: lignesDeLaPeriode,
    kpiVariables,
} = useKPI("source", getGraphConfigList)

// ================================
// COMPLÉTUDE DE LA PÉRIODE (DEV-1650 / DEV-1661)
// ================================
/**
 * Décision de Mathieu du 08/09 : « Il faut pas que l'outil devienne une bannière à
 * messages. L'affichage des trous, c'est plutôt un indicateur sur la page des
 * données brutes de la source / une manière de représenter la donnée à la date du
 * trou avec une légende qu'un ensemble de messages. »
 *
 * D'où cet indicateur : la période affichée, jour par jour, avec l'état de chaque
 * jour et une légende de quatre mots. Il ne coûte AUCUNE requête — il lit les
 * lignes que `useKPI` a déjà reçues et le verdict que `useWorkflowEnedisMessage` a
 * déjà chargé.
 */

/** Le verdict du compteur affiché (le composable est déjà filtré sur lui). */
const verdictDeLaSource = computed(
    () => workflowEnedisList.value?.[0]?.verdict ?? null
)

/**
 * Garde d'affichage : tant qu'aucun fait de trou n'est SERVI, la page reste
 * strictement celle d'avant. Les faits qui arment sont retenus en amont par des
 * interrupteurs désarmés (`ENEDIS_TROUS_CONNUS`, `ENEDIS_JUGE_SUR_DUS`) : aucune
 * modification du rendu de production tant qu'ils ne sont pas armés.
 */
const montreLaCompletude = computed(() =>
    unFaitDeTrouEstServi(verdictDeLaSource.value)
)

/** La complétude prête à peindre, ou `null` (rien n'est alors affiché). */
const completudeDeLaSource = computed(() => {
    if (!montreLaCompletude.value) return null
    const [debut, fin] = graphStore.period
    const debutISO = formatDate(debut, "YYYY-MM-DD")
    const finISO = formatDate(fin, "YYYY-MM-DD")
    if (!debutISO || !finISO) return null
    return completudeDeLaPeriode({
        debut: debutISO,
        fin: finISO,
        lignes: lignesDeLaPeriode.value,
        variables: kpiVariables.value,
        plages: plagesDuVerdict(verdictDeLaSource.value),
    })
})

/**
 * ⚠️ IL N'Y A PLUS DE COMPLÉTUDE CALCULÉE ICI POUR LA v2 (DEV-1508 lot 3c).
 *
 * Cette page en calculait une et la servait à la vue. Elle la calculait sur les
 * lignes des INDICATEURS (`useKPI`), sur la période du store et sur les
 * variables des graphiques sélectionnés : un autre jeu de séries et un autre
 * référentiel de jours que ceux que la vue charge elle-même. Résultat servi en
 * direct le 11/09 sur staging : « 0 / 93 jours mesurés (0 %) » sur un compteur
 * dont le graphe Énergie montrait, au même écran, des mesures du 11 au 28/06.
 *
 * La vue calcule donc sa bande, des séries qu'elle vient de charger, comme sur
 * l'adresse ombre — et la prop qui permettait d'en servir une autre a été
 * retirée, pour qu'aucun écran appelant ne puisse refabriquer l'écart.
 *
 * `completudeDeLaSource` ci-dessus est intacte : c'est celle de l'ANCIENNE page,
 * gardée par `montreLaCompletude`, et l'interrupteur désarmé doit la retrouver
 * telle quelle (E11).
 */

/** Titre de la page pour le header (compteur + identifiant). */
const getPageTitle = computed(() =>
    t("title", {
        idtype: currentSource.value?.type,
    })
)

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Détermine la fréquence initiale à utiliser en fonction du type de source
 * et des paramètres de l'URL.
 * @returns La fréquence initiale (raw, daily ou depuis l'URL)
 */
function determineInitialFreq(): Freq {
    const source = currentSource.value
    if (
        source?.type &&
        SOURCE_WITHOUT_RAW_FREQ.includes(source.type) &&
        graphStore.freq === "raw"
    ) {
        return "daily"
    }
    if (
        source?.type &&
        SOURCE_WITHOUT_HOURLY_FREQ.includes(source.type) &&
        graphStore.freq === "hourly"
    ) {
        return "daily"
    }
    if (route.query.freq && typeof route.query.freq === "string") {
        return route.query.freq as Freq
    }
    return "raw"
}

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de source et charge la source si nécessaire
 */
watch(
    () => route.params?._id_source,
    async (newId) => {
        if (!newId) return
        const id = newId as string
        fetchInitialData(id)
    },
    { immediate: true }
)

/**
 * Une fois la source et la liste des graphiques chargés,
 * initialise freq et graphStore.graphs (localStorage ou liste par défaut).
 */
watch(
    () => isFetchingInitialData.value,
    (fetching) => {
        if (!fetching && sourceIdParam.value && graphStore.isGraphListLoaded) {
            const source = sourceStore.getSourceById(sourceIdParam.value)
            if (
                source?.informations?.pdl?.etat !== "en_service" &&
                source?.type === SOURCES_TYPES.PDL
            ) {
                return
            }
            const initiale = determineInitialFreq()
            graphStore.updateGraphList(
                graphStore.getOptimalSourceGraph.length > 0
                    ? graphStore.getOptimalSourceGraph
                    : getCardGraphOptionList.value
            )
            // DEV-2071 : une fréquence venue de l'URL est gardée si un
            // graphique sélectionné la propose ; sinon, et sans choix, la plus
            // fine proposée pour laquelle la source a des données.
            graphStore.freq =
                frequencePourLesGraphiques(
                    route.query.freq ? initiale : null
                ) ?? initiale
            fetchKPIValue()
        }
    },
    { immediate: true }
)

/**
 * DEV-2071 : quand la sélection de graphiques change, une fréquence qu'aucun
 * graphique sélectionné ne propose bascule sur la plus fine qui en a. Rien ne
 * bouge tant qu'au moins un graphique sait tracer la fréquence courante.
 */
watch(getGraphiquesPourFrequence, () => {
    if (isFetchingInitialData.value) return
    const suivante = frequencePourLesGraphiques(graphStore.freq)
    if (suivante && suivante !== graphStore.freq) {
        graphStore.freq = suivante
    }
})

// ================================
// INTERACTIONS
// ================================
/**
 * Met à jour la liste des graphiques et recharge les KPI.
 * @param graphs - Liste des graphiques sélectionnés
 */
function onUpdateGraphList(graphs: string[]) {
    graphStore.updateGraphList(graphs)
    fetchKPIValue()
}

/**
 * Ajoute tous les graphiques disponibles au sélecteur
 */
function onClickAddOptimalGraphs() {
    graphStore.updateGraphList(getCardGraphOptionList.value)
    setTimeout(() => {
        fetchKPIValue()
    }, 500)
}

/**
 * Indique si le consentement de la source est invalide ou non
 * Si ce n'est pas un PDL ou PCE alors le consentement est valide.
 */
const isConsentInvalid = computed(() => {
    if (
        ![SOURCES_TYPES.PDL, SOURCES_TYPES.PCE].includes(
            currentSource.value?.type as string
        )
    ) {
        return false
    }
    // Vérifie le consentement dans le cas d'une source de type PDL,PCE
    return (
        !currentSource.value?.consentement ||
        currentSource.value?.consentement?.dismissed ||
        !currentSource.value?.consentement?.signed ||
        new Date(currentSource.value?.consentement?.date_to) < new Date()
    )
})

/**
 * Indique si l'utilisateur posséde le droit own
 */
const isRightOwnsOnly = computed(() => {
    // si le droit owns est le seul présent dans le tableau des droits alors renvoie true
    return currentSource.value?.rights?.every((right) => right === "owns")
})

/**
 * Indique si l'utilisateur possède le droit de télécharger les exports
 */
const hasExport = computed(() => {
    return (
        !isConsentInvalid.value &&
        !isRightOwnsOnly.value &&
        authStore.isSubscriptionActive
    )
})

/**
 * Indique si l'utilisateur possède le droit de recharger les données
 * 1 - Il est sur un abonnement actif EA avec un consentement valide et pas seulement le droit owns
 * 2 - Il a un consentement valide et pas seulement le droit owns
 * et n'est pas sur un abonnement actif EA sur une source qui n'est pas un compteur ou une station météo
 */
const hasRightToReloadData = computed(() => {
    return (
        !isConsentInvalid.value &&
        authStore.isSubscriptionActive &&
        (authStore.subscriptionPlan === "EA" ||
            ![
                SOURCES_TYPES.PDL,
                SOURCES_TYPES.PCE,
                SOURCES_TYPES.OPENMETEO,
                SOURCES_TYPES.METEOFRANCE,
                SOURCES_TYPES.METEO,
            ].includes(currentSource.value?.type as string))
    )
})

/** Contrôle l'affichage de la dialog d'export. */
const showExportDialog = ref(false)

/**
 * Ouvre la dialog d'export des données de la source.
 */
function onClickExport() {
    showExportDialog.value = true
}

/**
 * Lance la collecte on-demand des données pour la source courante.
 *
 * DEV-527 / DEV-16199 — la collecte passe désormais par api-metier
 * (`POST /task/update/source`, routage source→collecteur : un PCE grdf part
 * vers harvest, les autres opérateurs gardent le collecteur legacy). On suit le
 * `task_id` renvoyé (toast + subscribe), au lieu d'enfiler directement le
 * `getlastdata` legacy — qui est **no-opé pour grdf** côté data (le bouton
 * « Recharger » ne faisait donc plus rien sur les compteurs gaz).
 *
 * Mise à jour **incrémentale** (depuis la dernière date + marge) : l'utilisateur
 * n'a pas besoin de lancer un rebuild complet. Les PDL Enedis conservent leur
 * comportement historique (rebuild de l'app depuis les données workers).
 *
 * DEV-1436 — **un `task_id` absent n'est pas une erreur.** Depuis le
 * rebranchement DEV-1393 (19/08), un PDL Enedis part sur la refonte
 * (`ApiEnedisService.triggerUpdateOne` → `POST /onboard`), qui répond
 * `task_id: null` en régime **nominal** : le broker Redis refuse les connexions
 * depuis le web (REX DEV-773), la route bascule en mode `inline` et le travail
 * est **déjà fait** quand l'appel revient. Il n'y a donc aucune tâche Celery à
 * suivre. Traiter cette absence comme une erreur jetait un succès : le clic
 * aboutissait côté serveur et l'écran ne montrait rien — le bouton paraissait
 * mort pour les clients EA (seuls à le voir sur un PDL, cf. `hasRightToReloadData`).
 *
 * Les deux régimes doivent **dire quelque chose à l'utilisateur**. Le chemin
 * asynchrone est déjà couvert : `taskStore.followEvent` pousse une notification
 * de suivi (`task.store.ts` → `createNotifcation`). Le régime inline n'avait, lui,
 * aucun retour : le bouton est masqué pendant `isFetchingInitialData` (`v-if`),
 * puis réapparaît — un clignotement muet, indistinguable d'un clic sans effet
 * pour un client déjà convaincu que le bouton est mort (DEV-1436). On émet donc
 * un succès explicite sur le bus `notification` — et pas un `toast.add()` nu :
 * tous les conteneurs `<BaseToast>` montés par `App.vue` sont **groupés**
 * (`notifications` / `task` / `tasks`), un message sans `group` ne s'afficherait
 * nulle part. Symétriquement, l'échec réel ne partait qu'en `console.error` :
 * il est désormais visible lui aussi.
 */
async function onClickReload() {
    const id = sourceIdParam.value
    if (!id) return
    const source = currentSource.value
    if (!source) return

    const idKind = (source.idtype ?? source.type) as string
    const rebuild = idKind === SOURCES_TYPES.PDL

    try {
        const { task_id } = await sourceService.triggerSourceDataUpdate(id, {
            rebuild,
        })
        if (!task_id) {
            // Exécution synchrone côté collecteur : rien à suivre, on rafraîchit
            // puis on le dit — c'est le seul retour dont dispose ce régime.
            await fetchInitialData(id)
            notificationBus.emit({
                severity: "success",
                title: t("reload_success_summary"),
                message: t("reload_success_detail"),
                life: 6000,
            })
            return
        }
        taskStore.followEvent(task_id, task_id, {
            taskname: TASK_REBUILD_SOURCE_DATA,
            autoDelete: true,
            onSuccess: async () => {
                await fetchInitialData(id)
            },
        })
    } catch (error) {
        console.error(
            "Erreur lors du lancement de la tâche de rechargement des données",
            error
        )
        notificationBus.emit({
            severity: "error",
            title: t("reload_error_summary"),
            message: t("reload_error_detail"),
            life: 6000,
        })
    }
}

/**
 * Retire un graphique de la liste affichée.
 * @param graphId - Identifiant du graphique à retirer
 */
function onClickRemove(graphId: string): void {
    onUpdateGraphList(graphStore.graphs.filter((graph) => graph !== graphId))
}

onBeforeUnmount(() => {
    graphStore.cancelPendingGraphRequests()
    graphStore.setCurrentSourceId(null)
})
</script>
<template>
    <div class="page page-source-data">
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <HeaderPageSource
            :title="getPageTitle"
            :source-id="sourceIdParam"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <div class="page__header-action flex items-center gap-2">
                <BaseButton
                    v-if="hasExport"
                    :label="t('export')"
                    size="small"
                    icon="pi pi-download"
                    :disabled="isFetchingInitialData"
                    @click="onClickExport"
                />
                <BaseButton
                    v-if="hasRightToReloadData && !isFetchingInitialData"
                    icon="pi pi-sync"
                    size="small"
                    severity="secondary"
                    v-tooltip.left="t('reload')"
                    rounded
                    :aria-label="t('reload')"
                    :disabled="isFetchingInitialData"
                    @click="onClickReload"
                />
            </div>
        </HeaderPageSource>

        <ExportSourceDialog
            v-if="sourceIdParam"
            v-model:visible="showExportDialog"
            :_id_source="sourceIdParam"
        />

        <template v-if="isFetchingInitialData">
            <BaseSkeleton width="100%" height="3rem" />
            <BaseSkeleton width="100%" height="40rem" />
            <BaseSkeleton width="100%" height="40rem" />
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template v-else>
            <!-- Message important en haut -->
            <BaseAppMessage
                v-if="getGlobalMessage?.position === 'top'"
                v-bind="getGlobalMessage"
            />

            <!-- Messages workflow Enedis (état de récupération + avancement) -->
            <!-- Rien de nouveau ailleurs (E10) : la page v2 ne porte PAS cette
                 bannière. Elle disait « Le compteur … ne nous a plus transmis de
                 mesures depuis le 28/06/2026 », juste au-dessus de la carte
                 « Le compteur » qui porte déjà les quatre lignes de fraîcheur du
                 MÊME verdict — le même fait, écrit deux fois, dont une en
                 bannière. Règle de Mathieu du 08/09 : un trou ou un retard se
                 représente DANS la donnée. Interrupteur désarmé, la bannière est
                 strictement celle d'avant (E11). -->
            <div
                v-if="getWorkflowEnedisMessageList.length > 0 && !isEnedisPdlV2"
                class="page-source-data__workflow-enedis flex flex-col gap-4"
            >
                <DataChartMessage
                    v-for="(message, index) in getWorkflowEnedisMessageList"
                    :key="index"
                    class="!w-full"
                    :message="message.text"
                    :current-source-id="sourceIdParam"
                    :closable="true"
                    :severity="message.severity"
                />
            </div>

            <!-- ============================================ -->
            <!-- GRDF gaz (PCE) : page de données BRUTES dédiée (DEV-595) -->
            <!-- ============================================ -->
            <template v-if="isGrdfGas">
                <!-- Skeleton uniquement au 1er chargement (aucune donnée encore). -->
                <BaseSkeleton
                    v-if="grdfLoading && !grdfSources.length"
                    width="100%"
                    height="40rem"
                />
                <!-- Erreur seulement si on n'a jamais rien pu afficher. -->
                <div
                    v-else-if="grdfError && !grdfSources.length"
                    class="py-8 text-center text-red-500"
                >
                    {{ t("grdf_error") }}
                </div>
                <!-- Sinon : composant MONTÉ en continu (overlay pendant refetch)
                     → l'état (vue, sélecteurs, toggle) persiste entre périodes. -->
                <SourceDataGrdfView
                    v-else
                    :pce-number="
                        formatIdSource(currentSource?.idsource ?? '', 'pce')
                    "
                    :period="graphStore.period"
                    :sources="grdfSources"
                    :loading="grdfLoading"
                    @update:period="onGrdfPeriod"
                />
            </template>

            <!-- ============================================ -->
            <!-- Enedis (PDL) : page « Données du compteur » v2 (DEV-1508) -->
            <!-- Interrupteur désarmé → cette branche n'existe pas (E11). -->
            <!-- ============================================ -->
            <template v-else-if="isEnedisPdlV2">
                <SourceDataEnedisView
                    entree="standard"
                    :id-source="currentSource?._id_source ?? null"
                    :prm="formatIdSource(currentSource?.idsource ?? '', 'pdl')"
                    :informations="informationsPdl"
                    :verdict="verdictDeLaSource"
                    :segment="segmentPdl"
                    :directions="directionsPdl"
                    :variables-servies="variablesServiesPdl"
                    :contexte="contextePdl"
                    :jours-contexte="joursDuContextePdl"
                    :verdict-recu="verdictRecu"
                    @update:jours-contexte="joursDuContextePdl = $event"
                />
            </template>

            <!-- ============================================ -->
            <!-- Cas générique (autres types de source) -->
            <!-- ============================================ -->
            <template v-else>
                <!-- En-tête filtres (fréquence, période, graphiques) -->
                <DataHeader
                    :option-graphs="getCardGraphOptionList"
                    v-model:freq="graphStore.freq"
                    v-model:graphs="graphStore.graphs"
                    v-model:period="graphStore.period"
                    :freqs="getAvailableFreqs"
                    @update:graphs="onUpdateGraphList"
                    @update:period="fetchKPIValue"
                    @update:freq="fetchKPIValue"
                />
                <!-- Complétude de la période : les trous, à leur date, dans
                     la donnée (DEV-1650). Muet tant qu'aucun fait n'est servi. -->
                <DataCompletudeBar
                    v-if="completudeDeLaSource"
                    :completude="completudeDeLaSource"
                />
                <!-- Indicateurs KPI -->
                <DataIndicatorList
                    v-if="indicators.length > 0"
                    :indicators="indicators"
                    :error="errorKPI"
                    :loading="loadingKPI"
                    @click:kpi="onClickKPI"
                />
                <!-- Liste des graphiques -->
                <DataChartListItem
                    v-for="{ graphId, graphHidden } of getGraphSelected"
                    :key="graphId"
                    :graph-id="graphId"
                    type="source"
                    :source="currentSource"
                    :graph-hidden="graphHidden"
                    @click:remove="onClickRemove(graphId)"
                />
            </template>

            <!-- Message important en bas -->
            <BaseAppMessage
                v-if="getGlobalMessage?.position === 'bottom'"
                v-bind="getGlobalMessage"
            />
        </template>
    </div>
</template>
